import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { woundsizeStudioConfig } from '@modules/forms/form-modules/woundtrack/config/woundtrack-studio.config';
import { ComplianceLogoComponent } from '@modules/forms/form-modules/woundtrack/modules/compliance-logo/compliance-logo.component';
import { WoundsizeService } from '@modules/forms/form-modules/woundtrack/services/woundsize.service';
import { WoundtrackExportService } from '@modules/forms/form-modules/woundtrack/services/woundtrack-export.service';
import {
  getWoundsizeFileName,
  getWoundtrackFileName,
} from '@modules/forms/form-modules/woundtrack/utils/woundtrack-export.utils';
import { ModuleResultComponent } from '@modules/shared/module-result/module-result.component';
import { TopBarConfig } from '@modules/shared/top-bar/top-bar-config';
import { StudioConfig } from '@modules/studio/models/studio-config';
import { StudioEventService } from '@modules/studio/studio/studio-event.service';
import { StudioExportService } from '@modules/studio/studio/studio-export.service';
import { StudioMetadataService } from '@modules/studio/studio/studio-metadata.service';
import { StudioComponent } from '@modules/studio/studio/studio.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventTrackerService } from '@shared/event-tracker/services/event-tracker.service';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { base64ToFile, blobToBase64, fetchBlob } from '@shared/utils/utils';
import { TuiDialogContext } from '@taiga-ui/core';
import { TuiCardLarge } from '@taiga-ui/layout';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { combineLatest, first, Observable, of, switchMap, tap } from 'rxjs';
import {
  WoundsizeModuleComponentInputs,
  WoundsizeModuleComponentOutputs,
} from '../../models/woundsize-module-component';
import { WoundsizeTutorialService } from '../../services/woundsize-tutorial.service';
import { WoundsizeTutorialComponent } from '../woundsize-tutorial/woundsize-tutorial.component';

type WoundsizeContext = TuiDialogContext<WoundsizeModuleComponentOutputs, WoundsizeModuleComponentInputs>;
@UntilDestroy()
@Component({
  selector: 'pxc-woundsize',
  imports: [
    CommonModule,
    StudioComponent,
    TuiCardLarge,
    ModuleResultComponent,
    WoundsizeTutorialComponent,
    TranslocoDirective,
    ComplianceLogoComponent,
  ],
  providers: [
    StudioEventService,
    StudioExportService,
    StudioMetadataService,
    WoundsizeService,
    WoundsizeTutorialService,
    WoundtrackExportService,
  ],
  host: {
    class: 'block h-screen',
  },
  templateUrl: './woundsize.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WoundsizeComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly woundsizeService = inject(WoundsizeService);
  private readonly woundtrackExportService = inject(WoundtrackExportService);
  private readonly tutorialService = inject(WoundsizeTutorialService);
  private readonly eventTracker = inject(EventTrackerService);
  private readonly context = inject<WoundsizeContext>(POLYMORPHEUS_CONTEXT);

  private readonly woundsizeResult$ = this.woundsizeService.woundsizeResult$;
  private readonly areWoundsizeButtonsDisabled$ = this.woundsizeService.areWoundsizeButtonsDisabled$;

  targetModuleId: WoundsizeModuleComponentInputs['targetModuleId'] = this.context.data?.targetModuleId;
  picture: WoundsizeModuleComponentInputs['picture'] = this.context.data?.picture;
  isNewPicture: WoundsizeModuleComponentInputs['isNewPicture'] = this.context.data?.isNewPicture;

  woundsizeResultItems$ = this.woundsizeService.woundsizeResultItems$;

  get woundtrackPicture$(): Observable<string> {
    return fetchBlob(this.picture.source).pipe(
      switchMap((blob) => blobToBase64(blob)),
      untilDestroyed(this),
    );
  }

  get woundtrackResultPicture$(): Observable<string> {
    return this.woundtrackExportService.getExportPicture(this.targetModuleId);
  }

  get studioConfig(): StudioConfig {
    return { ...woundsizeStudioConfig };
  }

  get topBarConfig(): TopBarConfig {
    return {
      title: this.translocoService.translate('Détourer la lésion'),
      leftActions: [
        {
          id: MenuActionId.BACK,
          hint: this.translocoService.translate('Quitter'),
          icon: '@tui.arrow-left',
          execute: (): void => {
            this.context.completeWith(null);
          },
        },
        {
          id: MenuActionId.STUDIO_DOWNLOAD,
          hint: this.translocoService.translate('Télécharger la photo'),
          icon: '@tui.download',
          execute: (): void => {
            this.woundsizeService.downloadPicture();
          },
        },
      ],
      rightActions: [
        {
          id: MenuActionId.NEXT,
          label: this.translocoService.translate('Terminer'),
          icon: '@tui.check',
          execute: (): void => {
            combineLatest([this.woundsizeResult$, this.woundtrackPicture$, this.woundtrackResultPicture$])
              .pipe(
                first(),
                tap(() => this.eventTracker.trackWoundtrackI(this.targetModuleId, this.picture)),
              )
              .subscribe({
                next: ([woundsizeResult, woundtrackPicture, woundsizePicture]) => {
                  this.context.completeWith({
                    ...woundsizeResult,
                    pictures: [
                      ...(this.isNewPicture ? [base64ToFile(woundtrackPicture, getWoundtrackFileName())] : []),
                      base64ToFile(woundsizePicture, getWoundsizeFileName()),
                    ],
                  });
                },
              });
          },
        },
        {
          id: MenuActionId.HELP,
          label: this.translocoService.translate('Aide'),
          icon: '@tui.circle-help',
          condition: () => !this.tutorialService.showTutorial(),
          execute: () => this.tutorialService.open(),
        },
      ],
      rightButtons: [
        {
          icon: '@tui.circle-help',
          iconOnly: true,
          hint: this.translocoService.translate('Aide'),
          actionIds: [MenuActionId.HELP],
        },
        {
          title: this.translocoService.translate('Terminer'),
          appearance: 'primary',
          disabled$: this.areWoundsizeButtonsDisabled$,
          hintOnDisabled$: of(
            this.translocoService.translate<string>('Veuillez fermer le contour de la lésion pour continuer'),
          ),
          actionIds: [MenuActionId.NEXT],
        },
      ],
    };
  }

  ngOnInit(): void {
    if (this.picture?.arucoSurface) {
      this.woundsizeService.setWoundsizeWorker(this.picture.arucoSurface);
    }
  }
}
